import { Booking } from '@app/domain/Booking'
import { BookingReason } from '../../../../domain/BookingReason'
import { Event } from '@app/domain/VenueEvent'
import { QuestionType } from '@app/domain/Question'
import { ValidatorFn, Validators } from '@angular/forms'
import { Venue } from '@app/domain/Venue'

export interface BookingDetailsViewModelDelegate {

    showChangeBookingEndTimeOptions(): void
    showChangeBookingReasonOptions(): void
    showChangeBookingEventOptions(): void
    updateBookingTime(time: Date): void
    updateBookingReason(reason: BookingReason | null): void
    updateBookingEvent(event: Event | null): void
    updateCustomerMessage(message: string | null): void
    updateMerchantNotes(notes: string | null): void
    updatePartySize(size: number): void
    updateRequiresWheelchairAccess(requiresWheelchairAccess: boolean): void
    updateRequiresDogFriendly(requiresDogFriendly: boolean): void
    updateRequiresPramAccessible(requiresPramAccessible: boolean): void
    updateRequiresHighchairFriendly(requiresHighchairFriendly: boolean): void
    updateTextQuestionAnswer(questionId: string, answer: string | null): void
    goToCustomer(): void
}

export class BookingDetailsViewModel {

    bookingName: string | null
    bookingSize: number
    bookingNotes: string | null
    bookingReason: string | null
    bookingReasonCalendarColour: string | null
    bookingEvent: string | null
    canAcceptBooking: boolean = false
    canChangeTime!: boolean
    canGoToCustomer: boolean
    checkboxQuestions: { question: string, ticked: boolean }[]
    textQuestions: { questionId: string, question: string, answer: string, index: number, isEditable: boolean }[]
    multipleChoiceQuestions: { question: string, icon?: string, options: string[] }[]
    isEditingBooking: boolean = false
    partySizePluralMapping = {
        '=0': 'No People',
        '=1': '1 Person',
        other: '# People',
    }
    partySizeValidators: ValidatorFn [] = [
        Validators.required,
        Validators.min(1),
    ]
    reasonsReservableOnBookingDate: BookingReason[]
    eventsReservableOnBookingDate: Event[]
    showHidePrivateNotesButton: boolean = false
    showShowPrivateNotesButton: boolean = false
    hidePrivateNotes: boolean
    requiresWheelchairAccess: boolean
    requiresDogFriendly: boolean
    requiresPramAccessible: boolean
    requiresHighchairFriendly: boolean
    showWheelchairAccessOption: boolean
    showDogFriendlyOption: boolean
    showPramAccessibleOption: boolean
    showHighchairFriendlyOption: boolean
    showAccessibilitySection: boolean
    showVip: boolean = false
    selectedTablesPartySizeOptions: number[] | null

    constructor(
        private venue: Venue,
        private booking: Booking,
        hidePrivateNotesEnabled: boolean,
        private delegate: BookingDetailsViewModelDelegate
    ) {
        this.bookingName = this.makeBookingName()
        this.bookingSize = this.booking.size
        this.bookingNotes = this.booking.notes
        const reason = this.booking.reasonId ? this.venue.reasonWithId(this.booking.reasonId) : null
        this.bookingReason = reason?.displayName ?? null
        this.bookingReasonCalendarColour = reason?.calendarColour ?? null
        const event = this.booking.eventId ? this.venue.eventWithId(this.booking.eventId) : null
        this.bookingEvent = event?.displayName ?? null
        this.canAcceptBooking = booking.status().canAcceptBooking()
        this.canChangeTime = booking.status().canModifyBooking()
        this.canGoToCustomer = booking.customer !== null

        this.checkboxQuestions = this.booking.answers
            .filter(answer => answer.question.isTickable)
            .map(answer => {
                return {
                    question: answer.question.bookingDisplayText,
                    ticked: answer.isTicked(),
                }
            })
        this.textQuestions = this.booking.answers
            .filter(answer => answer.question.type === QuestionType.Dropdown || answer.question.type === QuestionType.Text || answer.question.type === QuestionType.DOB)
            .map((answer, index) => {
                return {
                    questionId: answer.question.id,
                    question: answer.question.bookingDisplayText,
                    answer: answer.answer,
                    index: index,
                    isEditable: answer.question.type === QuestionType.Text,
                }
            })
        this.multipleChoiceQuestions = this.booking.answers
            .filter(answer => answer.question.type === QuestionType.DietaryRestrictions || answer.question.type === QuestionType.DietaryRestrictionsText)
            .filter(answer => answer.answer !== 'None')
            .map(answer => {
                return {
                    question: answer.question.bookingDisplayText,
                    icon: 'assets/dietary-restrictions/normal.svg',
                    options: answer.answer.split(',').map(option => option.trim()),
                }
            })
        this.reasonsReservableOnBookingDate = this.venue.reasonsReservableOnDate(this.booking.start)
        this.eventsReservableOnBookingDate = this.venue.eventsReservableOnDate(this.booking.start)
        this.hidePrivateNotes = hidePrivateNotesEnabled && booking.merchantNotes !== null
        this.showHidePrivateNotesButton = false
        this.showShowPrivateNotesButton = hidePrivateNotesEnabled && booking.merchantNotes !== null
        this.requiresWheelchairAccess = booking.requiresWheelchairAccess
        this.requiresDogFriendly = booking.requiresDogFriendly
        this.requiresPramAccessible = booking.requiresPramAccessible
        this.requiresHighchairFriendly = booking.requiresHighchairFriendly
        this.showWheelchairAccessOption = venue.hasANonWheelchairAccessibleTable()
        this.showDogFriendlyOption = venue.hasANonDogFriendlyTable()
        this.showPramAccessibleOption = venue.hasANonPramAccessibleTable()
        this.showHighchairFriendlyOption = venue.hasANonHighchairFriendlyTable()
        this.showAccessibilitySection = this.showWheelchairAccessOption || this.showDogFriendlyOption || this.showPramAccessibleOption || this.showHighchairFriendlyOption
        this.showVip = booking.customer?.vip ?? false
        this.selectedTablesPartySizeOptions = this.buildSelectedTablesPartySizeOptions()
    }

    showChangeBookingTimeOptions() {
        this.delegate.showChangeBookingEndTimeOptions()
    }

    showChangeBookingReasonOptions() {
        this.delegate.showChangeBookingReasonOptions()
    }

    showChangeBookingEventOptions() {
        this.delegate.showChangeBookingEventOptions()
    }

    updateBookingTime(time: Date) {
        this.delegate.updateBookingTime(time)
    }

    updateBookingReason(reason: BookingReason | null) {
        this.delegate.updateBookingReason(reason)
    }

    updateBookingEvent(event: Event | null) {
        this.delegate.updateBookingEvent(event)
    }

    updateCustomerMessage(message: string | null) {
        this.delegate.updateCustomerMessage(message)
    }

    updateMerchantNotes(notes: string | null) {
        this.delegate.updateMerchantNotes(notes)
    }

    updatePartySize(size: number) {
        this.delegate.updatePartySize(size)
    }

    updateTextQuestionAnswer(questionId: string, answer: string | null) {
        this.delegate.updateTextQuestionAnswer(questionId, answer)
    }
    
    toggleRequiresWheelchairAccess() {
        this.delegate.updateRequiresWheelchairAccess(!this.requiresWheelchairAccess)
    }
    
    toggleRequiresDogFriendly() {
        this.delegate.updateRequiresDogFriendly(!this.requiresDogFriendly)
    }

    toggleRequiresPramAccessible() {
        this.delegate.updateRequiresPramAccessible(!this.requiresPramAccessible)
    }

    toggleRequiresHighchairFriendly() {
        this.delegate.updateRequiresHighchairFriendly(!this.requiresHighchairFriendly)
    }

    togglePrivateNotesVisibility() {
        this.hidePrivateNotes = !this.hidePrivateNotes
        this.showHidePrivateNotesButton = !this.showHidePrivateNotesButton
        this.showShowPrivateNotesButton = !this.showShowPrivateNotesButton
    }

    goToCustomer() {
        this.delegate.goToCustomer()
    }

    private makeBookingName() {
        const name = this.booking.name
        if (name.length === 0) {
            return null
        }
        return name
    }

    private buildSelectedTablesPartySizeOptions(): number[] | null {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return null
        }
        const tables = area.tablesUsedForBooking(this.booking)
        if (tables.length === 0) {
            return null
        }
        return area.partySizeOptionsForTables(tables)
    }
}
